'use client';

import { FacetCounts } from 'models/product';
import { Bike } from 'models/shared';
import { UserDocument, UserSizes } from 'models/user';
import Link from 'next/link';
import { CategoryPageSideFilters } from '@c/CategoryOrSubcategoryPage';
import { GearFilter } from './components/GearFilter';
import { GarageFilter } from './components/GarageFilter';
import { useMySizesGarage } from '@util/hooks/useMySizesGarage';

type MySizesGarageProps = {
  userDoc: UserDocument | null | undefined;
  facet_counts: FacetCounts | undefined;
  handleBikeSelection: (args: {
    bike?: Bike | null;
    remove?: boolean;
    bikes?: Bike[];
  }) => void;
  filterState: CategoryPageSideFilters;
  handleSizeSelection: (sizes: UserSizes) => void;
  bikePendingFilters?: {
    bikeTypes: string[] | undefined;
    bikeModels: string[] | undefined;
    bikeYears: string[] | undefined;
    bikeMakes: string[] | undefined;
  };
  sizePendingFilters?: {
    letterSizes: string[] | undefined;
    numberSizes: string[] | undefined;
  };
};

export default function MySizesGarage({
  userDoc,
  facet_counts,
  handleBikeSelection,
  filterState,
  handleSizeSelection,
  bikePendingFilters,
  sizePendingFilters,
}: MySizesGarageProps) {
  const {
    isMySizesEnabled,
    setIsMySizesEnabled,
    isMyGarageEnabled,
    setIsMyGarageEnabled,
    selectedBikes,
    setSelectedBikes,
    selectedSizes,
    isEditGarageModalOpen,
    setIsEditGarageModalOpen,
    isMobileEditGarageModalOpen,
    setIsMobileEditGarageModalOpen,
    isMobileEditSizesModalOpen,
    setIsMobileEditSizesModalOpen,
    isEditSizesModalOpen,
    setIsEditSizesModalOpen,
    isAddBikeModalOpen,
    setIsAddBikeModalOpen,
    topFilter,
    shouldShowBothFilters,
    filterVariant,
    onAddBikeClick,
    onSyncSelectedBikes,
    onSelectSize,
  } = useMySizesGarage({
    userDoc,
    facet_counts,
    handleBikeSelection,
    filterState,
    handleSizeSelection,
    bikePendingFilters,
    sizePendingFilters,
  });

  const userSizesFlatList: {
    type: keyof UserSizes;
    value: string | number | undefined;
    enabled: boolean;
  }[] = userDoc?.sizes
    ? [
        {
          type: 'helmet',
          value: userDoc.sizes.helmet,
          enabled: !!selectedSizes?.helmet,
        },
        {
          type: 'jersey',
          value: userDoc.sizes.jersey,
          enabled: !!selectedSizes?.jersey,
        },
        {
          type: 'pants',
          value: userDoc.sizes.pants,
          enabled: !!selectedSizes?.pants,
        },
        {
          type: 'boots',
          value: userDoc.sizes.boots,
          enabled: !!selectedSizes?.boots,
        },
      ]
    : [];

  if (!userDoc || userDoc.name === 'Anonymous User') {
    return (
      <div className="flex h-fit w-full flex-col items-start gap-6 border border-[#D9D9D9] p-6">
        <h3 className="text-[1.8rem] font-semibold text-black">
          My sizes & garage
        </h3>

        <p className="text-[1.6rem] font-medium text-[#7F7F7F]">
          Sign up or log in to add your sizes and vehicles. We&apos;ll
          personalize your feed to show only listings that fit you and your
          ride.
        </p>

        <Link
          href={'/login'}
          className="bg-transparent text-[1.6rem] font-semibold capitalize text-brand-secondary transition-colors duration-150 hover:text-brand-secondary-darker"
        >
          Login Now
        </Link>
      </div>
    );
  }

  if (shouldShowBothFilters) {
    const filters =
      topFilter === 'gear' ? ['gear', 'parts'] : ['parts', 'gear'];

    return (
      <div className="flex w-full flex-col gap-6">
        {filters.map((filter) =>
          filter === 'gear' ? (
            <GearFilter
              key="gear-filter"
              isMySizesEnabled={isMySizesEnabled}
              setIsMySizesEnabled={setIsMySizesEnabled}
              isEditSizesModalOpen={isEditSizesModalOpen}
              setIsEditSizesModalOpen={setIsEditSizesModalOpen}
              isMobileEditSizesModalOpen={isMobileEditSizesModalOpen}
              setIsMobileEditSizesModalOpen={setIsMobileEditSizesModalOpen}
              userDoc={userDoc}
              userSizesFlatList={userSizesFlatList}
              selectedSizes={selectedSizes}
              onSelectSize={onSelectSize}
              handleSizeSelection={handleSizeSelection}
            />
          ) : (
            <GarageFilter
              key="garage-filter"
              isMyGarageEnabled={isMyGarageEnabled}
              setIsMyGarageEnabled={setIsMyGarageEnabled}
              isEditGarageModalOpen={isEditGarageModalOpen}
              setIsEditGarageModalOpen={setIsEditGarageModalOpen}
              isAddBikeModalOpen={isAddBikeModalOpen}
              setIsAddBikeModalOpen={setIsAddBikeModalOpen}
              userDoc={userDoc}
              selectedBikes={selectedBikes}
              setSelectedBikes={setSelectedBikes}
              handleBikeSelection={handleBikeSelection}
              onAddBikeClick={onAddBikeClick}
              onSyncSelectedBikes={onSyncSelectedBikes}
              isMobileEditGarageModalOpen={isMobileEditGarageModalOpen}
              setIsMobileEditGarageModalOpen={setIsMobileEditGarageModalOpen}
            />
          )
        )}
      </div>
    );
  }

  return filterVariant === 'gear' ? (
    <>
      {!!userSizesFlatList.length && (
        <GearFilter
          isMySizesEnabled={isMySizesEnabled}
          setIsMySizesEnabled={setIsMySizesEnabled}
          isEditSizesModalOpen={isEditSizesModalOpen}
          setIsEditSizesModalOpen={setIsEditSizesModalOpen}
          isMobileEditSizesModalOpen={isMobileEditSizesModalOpen}
          setIsMobileEditSizesModalOpen={setIsMobileEditSizesModalOpen}
          userDoc={userDoc}
          userSizesFlatList={userSizesFlatList}
          selectedSizes={selectedSizes}
          onSelectSize={onSelectSize}
          handleSizeSelection={handleSizeSelection}
        />
      )}
    </>
  ) : (
    <GarageFilter
      isMyGarageEnabled={isMyGarageEnabled}
      setIsMyGarageEnabled={setIsMyGarageEnabled}
      isEditGarageModalOpen={isEditGarageModalOpen}
      setIsEditGarageModalOpen={setIsEditGarageModalOpen}
      isAddBikeModalOpen={isAddBikeModalOpen}
      setIsAddBikeModalOpen={setIsAddBikeModalOpen}
      userDoc={userDoc}
      selectedBikes={selectedBikes}
      setSelectedBikes={setSelectedBikes}
      handleBikeSelection={handleBikeSelection}
      onAddBikeClick={onAddBikeClick}
      onSyncSelectedBikes={onSyncSelectedBikes}
      isMobileEditGarageModalOpen={isMobileEditGarageModalOpen}
      setIsMobileEditGarageModalOpen={setIsMobileEditGarageModalOpen}
    />
  );
}
